import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Coins, Route, Workflow } from "lucide-react";
import { Link } from "react-router-dom";
import { ErrorState, LoadingState } from "../components/AsyncState";
import { apiRequest } from "../lib/api";
import {
  applicationListSchema,
  healthSchema,
  normalizeState,
  runtimeHealthSchema,
  taskListSchema,
  usageSchema
} from "../lib/schemas";

export function StateBadge({ state }: { state: string }) {
  return (
    <span className={`state-badge state-${normalizeState(state)}`}>{normalizeState(state)}</span>
  );
}

function money(microunits: number) {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 4
  }).format(microunits / 1_000_000);
}

export function OverviewPage() {
  const health = useQuery({
    queryKey: ["ready"],
    queryFn: () => apiRequest("/health/ready", healthSchema),
    refetchInterval: 15_000
  });
  const tasks = useQuery({
    queryKey: ["tasks"],
    queryFn: () => apiRequest("/v1/admin/tasks?limit=8", taskListSchema),
    refetchInterval: 5_000
  });
  const runtime = useQuery({
    queryKey: ["runtime-health"],
    queryFn: () => apiRequest("/v1/admin/runtime/health", runtimeHealthSchema),
    refetchInterval: 15_000
  });
  const applications = useQuery({
    queryKey: ["applications"],
    queryFn: () => apiRequest("/v1/admin/applications", applicationListSchema)
  });
  const firstApplicationId = applications.data?.data[0]?.id;
  const usage = useQuery({
    queryKey: ["usage", firstApplicationId],
    queryFn: () =>
      apiRequest(`/v1/admin/applications/${firstApplicationId ?? ""}/usage`, usageSchema),
    enabled: Boolean(firstApplicationId),
    refetchInterval: 15_000
  });
  const error = health.error ?? tasks.error ?? runtime.error ?? usage.error;
  const active =
    tasks.data?.data.filter((task) => ["in_progress", "waiting"].includes(task.status)).length ?? 0;
  const attention = runtime.data?.durable_responses.needs_attention ?? 0;

  return (
    <main className="page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">Runtime pulse</p>
          <h1>Overview</h1>
        </div>
        <span className={`health-pill ${health.data?.status === "ready" ? "healthy" : "warning"}`}>
          <i /> {health.data?.status ?? "Checking"}
        </span>
      </div>
      {error instanceof Error && <ErrorState error={error} />}
      {!health.data && !error && <LoadingState label="Connecting to Synapse" />}
      <section className="metric-strip" aria-label="Gateway metrics">
        <Metric icon={Workflow} label="Active tasks" value={String(active)} />
        <Metric icon={AlertTriangle} label="Needs attention" value={String(attention)} />
        <Metric icon={Coins} label="Tracked cost" value={money(usage.data?.cost_microunits ?? 0)} />
        <Metric
          icon={Route}
          label="Pending approvals"
          value={String(runtime.data?.pending_approvals ?? 0)}
        />
      </section>
      <section className="section-band">
        <div className="section-heading">
          <div>
            <h2>Recent tasks</h2>
            <p>Durable runs ordered by creation.</p>
          </div>
          <Link to="/tasks">View all</Link>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Task</th>
                <th>Model</th>
                <th>State</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {tasks.data?.data.map((task) => (
                <tr key={task.id}>
                  <td>
                    <Link className="mono link" to={`/tasks/${task.id}`}>
                      {task.id}
                    </Link>
                  </td>
                  <td className="mono">{task.public_model}</td>
                  <td>
                    <StateBadge state={task.status} />
                  </td>
                  <td>{new Date(task.updated_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="section-band split-band">
        <div>
          <h2>Request accounting</h2>
          <p>
            {usage.data?.records ?? 0} records, {usage.data?.input_tokens ?? 0} input tokens,{" "}
            {usage.data?.output_tokens ?? 0} output tokens.
          </p>
        </div>
        <div>
          <h2>Runtime health</h2>
          <p>
            {runtime.data?.running_nodes ?? 0} running nodes, {runtime.data?.active_leases ?? 0}{" "}
            active leases, {runtime.data?.undelivered_outbox ?? 0} outbox events pending.
          </p>
        </div>
      </section>
    </main>
  );
}

function Metric({
  icon: Icon,
  label,
  value
}: {
  icon: typeof Workflow;
  label: string;
  value: string;
}) {
  return (
    <div className="metric">
      <span className="metric-icon">
        <Icon size={17} />
      </span>
      <span>
        <strong>{value}</strong>
        <small>{label}</small>
      </span>
    </div>
  );
}
